import { useClickOutside } from "@gromia/utils/useClickOutside";
import React from "react";

const states = {
  closed: 0,
  open: 1,
  opening: 2,
};

export type Actions = {
  onModalOpen?: () => void;
  onModalClose?: () => void;
  onFullscreenToggle?: () => void;
};

export function useModalState<T extends HTMLElement = never>(
  defaultIsOpen?: boolean,
) {
  const [state, setState] = React.useState(
    defaultIsOpen ? states.open : states.closed,
  );

  const [wasClosed, setWasClosed] = React.useState(false);

  React.useEffect(() => {
    if (state === states.opening) {
      requestAnimationFrame(() => {
        setState(states.open);
      });
    }
  });

  function onModalOpen() {
    setState(states.opening);
  }

  function onModalClose() {
    setState(states.closed);
    setWasClosed(true);
  }

  function foldStateL<T>(ifOpenOrOpening: () => T, ifClosed: () => T) {
    return state === states.open || state === states.opening
      ? ifOpenOrOpening()
      : ifClosed();
  }

  const fsTarget = React.useRef<HTMLDivElement>(null);

  function onFullscreenToggle() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    if (fsTarget.current) {
      fsTarget.current.requestFullscreen();
    }
  }

  const ref: React.RefObject<T> = useClickOutside<T>(onModalClose);
  const actions = { onModalOpen, onModalClose, onFullscreenToggle };

  return {
    actions,
    ref,
    foldStateL,
    isOpen: state === states.open,
    wasClosed,
    fsEnabled: () => document.fullscreenEnabled,
    fsTarget,
  };
}
